// src/cockpit4/workspaces/WorkflowWorkspace.tsx
// ============================================
// 经营中心（Operation Center）- 经营驾驶舱（v6 专业级重构）
//
// 定位（v5.0 声明）：不是"自动化工具"，而是"盯着目标、发现问题、建议行动"的经营者
// v6 落地：经营者视角放主位，工作流/扣子降为执行引擎
//
// 布局：
//   - 顶部：分组胶囊标签栏（经营看板 | 流程▾ | 协同▾ | 增长▾ | 扣子）
//     GEO+DSO 已由官网一级页面与系统顶部独立 Tab 承载，经营中心不再内嵌冗余入口
//   - 经营看板：今日目标(GoalBoard) + KPI 条 + 工作流控制台（内部工作流 + 扣子 Bot）
//              + 执行详情（DAG/步骤/审批，真实数据）+ 执行历史
//
// 原则：
//   - 白皮书铁律一：无数据不显示占位假条目（删除示例 DAG）
//   - 单容器单滚动，统一色板（紫主色 + 语义色）
// ============================================

import { useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Workflow as WorkflowIcon,
  RefreshCw,
  Circle,
  Play,
  XCircle,
  RotateCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Loader2,
  FileText,
  Terminal,
  CheckSquare,
  XSquare,
  Globe,
  Bot,
  X,
  // P2 图标
  Zap,
  GitBranch,
  Boxes,
  Sparkles,
  TrendingUp,
  Radar,
  Wallet,
  Store,
  Lightbulb,
  ListOrdered,
} from "lucide-react";
import { WorkflowLiveDag } from "../../components/Cockpit";
import { DAG_VIEWBOX, computeNodeXs, type DagExecution, type DagNode, type DagEdge } from "../../components/Cockpit/WorkflowLiveDag";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
// P0/P1 + GEO+DSO 新增组件导入
import WorkflowTemplateLibrary from "../components/WorkflowTemplateLibrary";
import WorkflowNodeEditor from "../components/WorkflowNodeEditor";
import WorkflowMonitor from "../components/WorkflowMonitor";
import SOPLibrary from "../components/SOPLibrary";
import WorkflowEfficiencyAnalyzer from "../components/WorkflowEfficiencyAnalyzer";
import HumanAICollaboration from "../components/HumanAICollaboration";
import ApprovalFlowEngine from "../components/ApprovalFlowEngine";
// P2-功能8：流程自动优化
import ProcessAutoOptimizer from "../components/ProcessAutoOptimizer";
// P2-功能9：事件驱动自动化
import EventDrivenAutomation from "../components/EventDrivenAutomation";
// P2-功能10：运营数字孪生
import OperationsDigitalTwin from "../components/OperationsDigitalTwin";
// 扣子(Coze)工作流集成（一级入口）
import CozeWorkflowPanel from "../components/CozeWorkflowPanel";
import HermesTaskPanel from "../../components/Hermes/Tasks/HermesTaskPanel";
import GoalBoardPanel from "../components/GoalBoardPanel";
// v6.3.9 官网管控中台（官网访问统计 + 评论管理）
import WebsiteControlPanel from "../components/WebsiteControlPanel";
// geo+DSO 已由系统顶部一级 Tab 与官网独立页面承载，经营中心不再内嵌
import BusinessAdvicePanel from "../components/BusinessAdvicePanel";
// v6.35：官网访问统计看板（访问量/独立IP/地区/来源/页面）
import WebsiteStatsPanel from "../components/WebsiteStatsPanel";
// v6.41：效果复盘看板（趋势折线 + 聚合统计 + 自动周报）
import EffectReviewPanel from "../components/EffectReviewPanel";
// Phase3 P0-3：页面顶部业务说明 + 跨模块跳转
import WorkspaceBizBar from "../components/WorkspaceBizBar";
import { useCockpit4Store } from "../store";
// v5.2 审计修复 P2：产品调度 + 决策支持
import ProductDispatcherPanel from "../components/ProductDispatcherPanel";
import DecisionSupportPanel from "../components/DecisionSupportPanel";
// 扣子 Bot 列表（并入工作流控制台）
import { cozeIntegration, type CozeBot } from "../../services/cozeIntegration";
import {
  WorkflowQuery,
  WorkflowCommand,
  WorkflowStatus,
  type Workflow,
  type WorkflowExecution,
  type StepResult,
} from "../../runtime/WorkflowRuntime/types";

// ============================================
// 步骤状态 → 颜色 / 中文标签
// ============================================

const STEP_COLOR_MAP: Record<string, string> = {
  completed: "#34D399",
  running: "#A78BFA",
  pending: "#6B7280",
  failed: "#F87171",
  skipped: "#9CA3AF",
  waiting_approval: "#FBBF24",
  canceled: "#6B7280",
};

const STEP_STATUS_LABEL: Record<string, string> = {
  completed: "已完成",
  running: "执行中",
  pending: "待执行",
  failed: "失败",
  skipped: "已跳过",
  waiting_approval: "待审批",
  canceled: "已取消",
};

const EXEC_STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  pending: "待执行",
  running: "执行中",
  paused: "已暂停",
  completed: "已完成",
  failed: "失败",
  canceled: "已取消",
  timeout: "已超时",
};

// ============================================
// 将 WorkflowExecution 转换为 DagExecution
// ============================================

function executionToDag(exec: any): DagExecution | null {
  if (!exec) return null;
  // 兼容字段名：executionId（标准）或 id（旧版）
  const execId = exec.executionId || exec.id;
  if (!execId) return null;
  const steps: any[] = Array.isArray(exec.stepResults)
    ? exec.stepResults
    : Array.isArray(exec.steps)
    ? exec.steps
    : [];
  if (steps.length === 0) return null;

  const xs = computeNodeXs(steps.length);
  const nodes: DagNode[] = steps.map((s, idx) => {
    const status = (s.status || "pending") as DagNode["status"];
    const color = STEP_COLOR_MAP[status] || "#6B7280";
    return {
      id: String(s.stepId ?? s.id ?? `step-${idx}`),
      label: String(s.stepName ?? s.name ?? s.runtime ?? `Step ${idx + 1}`),
      icon: Circle as any,
      status: ((status as string) === "waiting_approval" ? "pending" : status) as DagNode["status"],
      x: xs[idx],
      y: DAG_VIEWBOX.nodeY,
      duration: typeof s.duration === "number" ? s.duration : undefined,
      color,
    };
  });

  const edges: DagEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      from: nodes[i].id,
      to: nodes[i + 1].id,
      label: nodes[i + 1].label,
    });
  }

  const completedCount = nodes.filter((n) => n.status === "completed").length;
  return {
    workflowName: exec.workflowName || exec.workflowId || "Workflow",
    executionId: execId,
    completedNodes: completedCount,
    totalNodes: nodes.length,
    nodes,
    edges,
  };
}

// ============================================
// 输入参数类型推断
// ============================================

interface InputFieldConfig {
  key: string;
  type: "text" | "number" | "boolean" | "list";
  label: string;
  default: any;
}

function inferInputFields(workflow: Workflow | null): InputFieldConfig[] {
  if (!workflow?.inputs) return [];
  return Object.entries(workflow.inputs).map(([key, def]: [string, any]) => {
    // 兼容 DSL 格式 { type: 'string', default: '...', description: '...' }
    const explicitType = def?.type;
    const defaultVal = def?.default !== undefined ? def.default : def;
    let fieldType: InputFieldConfig["type"] = "text";
    if (explicitType === "boolean" || typeof defaultVal === "boolean") fieldType = "boolean";
    else if (explicitType === "number" || typeof defaultVal === "number") fieldType = "number";
    else if (explicitType === "array" || Array.isArray(defaultVal)) fieldType = "list";
    return {
      key,
      type: fieldType,
      label: def?.description || key,
      default: defaultVal,
    };
  });
}

function getDefaultInputs(workflow: Workflow | null): Record<string, any> {
  if (!workflow?.inputs) return {};
  const result: Record<string, any> = {};
  for (const [key, def] of Object.entries(workflow.inputs)) {
    const defaultVal = (def as any)?.default !== undefined ? (def as any).default : def;
    result[key] = defaultVal;
  }
  return result;
}

// ============================================
// 功能分组 Tab 定义（经营中心）
// ============================================

const GROUP_TABS: Record<string, { id: string; label: string; icon: typeof FileText }[]> = {
  流程: [
    { id: "templates", label: "模板库", icon: FileText },
    { id: "editor", label: "流程编辑器", icon: WorkflowIcon },
    { id: "monitor", label: "监控告警", icon: Circle },
  ],
  协同: [
    { id: "sop", label: "SOP库", icon: FileText },
    { id: "hermes_tasks", label: "任务中心", icon: ListOrdered },
    { id: "efficiency", label: "效率分析", icon: Globe },
    { id: "human_ai", label: "人机协同", icon: CheckSquare },
    { id: "approval", label: "审批流", icon: CheckCircle2 },
  ],
  增长: [
    { id: "optimize", label: "流程优化", icon: Zap },
    { id: "event", label: "事件自动化", icon: GitBranch },
    { id: "dispatcher", label: "产品调度", icon: Store },
    { id: "decision", label: "决策支持", icon: Lightbulb },
    { id: "digital_twin", label: "数字孪生", icon: Boxes },
    { id: "website", label: "官网管控", icon: Globe },
  ],
};

/** 扁平化所有功能 Tab（用于 pinned 显示） */
const ALL_FUNCTION_TABS: { id: string; label: string; icon: typeof FileText }[] = Object.values(GROUP_TABS).flat();

// v6.37：经营情报区 Tab 按钮（AI 经营建议 / 官网访问统计）
function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-t-md text-[10px] font-mono transition-colors border border-b-0 " +
        (active
          ? "bg-white/[0.06] text-cyan-300 border-white/[0.08]"
          : "bg-transparent text-gray-500 border-transparent hover:text-gray-300 hover:bg-white/[0.03]")
      }
    >
      {children}
    </button>
  );
}

// ============================================
// 主组件
// ============================================

export default function WorkflowWorkspace() {
  // ===== 状态 =====
  const [activeTab, setActiveTab] = useState<string>("workspace");
  // v6.37：经营情报区 Tab（AI 经营建议 / 官网访问统计 / 效果复盘）
  const [adviceTab, setAdviceTab] = useState<"advice" | "stats" | "review">("advice");
  // 用户从分组下拉中固定的功能 Tab（可关闭）
  const [pinnedTabs, setPinnedTabs] = useState<string[]>([]);
  // 当前展开的分组下拉
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  // Phase3 P1-3：经营中心「高级」工作流控制台（DAG/执行历史/调度）默认折叠，
  // 老板/运营默认只看业务经营建议区（经营情报区）；展开才显示技术向控制台。
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [loadingWf, setLoadingWf] = useState(true);
  const [loadingExec, setLoadingExec] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // ===== 扣子 Bot（并入工作流控制台） =====
  const [cozeBots, setCozeBots] = useState<CozeBot[]>([]);
  const [cozeConfigured, setCozeConfigured] = useState(false);

  useEffect(() => {
    const refresh = () => {
      setCozeBots(cozeIntegration.getBots());
      setCozeConfigured(cozeIntegration.isConfigured());
    };
    refresh();
    const unsub = cozeIntegration.subscribe(refresh);
    return unsub;
  }, []);

  // ===== 加载工作流列表 =====
  const loadWorkflows = useCallback(async () => {
    setLoadingWf(true);
    setError(null);
    try {
      // 等待 RuntimeManager 就绪
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!RuntimeManager.hasRuntime(RuntimeName.Workflow)) {
        setError("WorkflowRuntime 未注册");
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: WorkflowQuery.List,
      });
      // 双层解包：resp.data 是 WorkflowResult，resp.data.data 才是真实数据
      const inner = resp?.data as any;
      const list: Workflow[] = inner?.success
        ? (Array.isArray(inner.data) ? (inner.data as Workflow[]) : [])
        : (Array.isArray(resp?.data) ? (resp.data as Workflow[]) : []);
      setWorkflows(list);
      // 默认选中第一个
      if (list.length > 0 && !selectedWorkflowId) {
        setSelectedWorkflowId(list[0].id);
        setInputs(getDefaultInputs(list[0]));
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoadingWf(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== 加载执行历史 =====
  const loadExecutions = useCallback(async () => {
    setRefreshing(true);
    try {
      if (!RuntimeManager.hasRuntime(RuntimeName.Workflow)) return;
      const resp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: WorkflowQuery.ListExecutions,
        limit: 50,
      });
      // 双层解包：resp.data 是 WorkflowResult，resp.data.data 才是真实数据
      const inner = resp?.data as any;
      const list: WorkflowExecution[] = inner?.success
        ? (Array.isArray(inner.data) ? (inner.data as WorkflowExecution[]) : [])
        : (Array.isArray(resp?.data) ? (resp.data as WorkflowExecution[]) : []);
      // 按开始时间倒序
      list.sort((a, b) => {
        const ta = new Date(a.startedAt || 0).getTime();
        const tb = new Date(b.startedAt || 0).getTime();
        return tb - ta;
      });
      setExecutions(list);
      setLastUpdated(new Date().toISOString());
      // 自动选中最近一次 running 的执行
      const running = list.find((e) => e.status === WorkflowStatus.Running);
      if (running && !selectedExecutionId) {
        setSelectedExecutionId(running.executionId);
      } else if (list.length > 0 && !selectedExecutionId) {
        setSelectedExecutionId(list[0].executionId);
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoadingExec(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== 支持 URL ?tab=xxx 直达某个功能 Tab（如启动器"AI可见性"入口 ?tab=aivisibility） =====
  useEffect(() => {
    const parse = () => {
      try {
        const q = new URLSearchParams(window.location.search);
        const t = q.get("tab");
        if (t) {
          const flat = Object.values(GROUP_TABS).flat();
          if (flat.some((f) => f.id === t)) {
            setActiveTab(t);
            // 清理 URL 参数，避免刷新后状态丢失
            history.replaceState(null, "", window.location.pathname);
          }
        }
      } catch {
        // ignore
      }
    };
    parse();
    // 启动器入口通过 replaceState + popstate 触发（组件已挂载时也能响应）
    window.addEventListener("popstate", parse);
    return () => window.removeEventListener("popstate", parse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 初始化加载
  useEffect(() => {
    loadWorkflows();
    loadExecutions();
  }, [loadWorkflows, loadExecutions]);

  // 有运行中执行时，3 秒轮询
  const hasRunning = executions.some(
    (e) => e.status === WorkflowStatus.Running || e.status === WorkflowStatus.Paused
  );
  useEffect(() => {
    if (!hasRunning) return;
    const timer = setInterval(loadExecutions, 3000);
    return () => clearInterval(timer);
  }, [hasRunning, loadExecutions]);

  // ===== 选中的工作流 / 执行实例 =====
  const selectedWorkflow = useMemo(
    () => workflows.find((w) => w.id === selectedWorkflowId) || null,
    [workflows, selectedWorkflowId]
  );

  const selectedExecution = useMemo(() => {
    if (!selectedExecutionId) return null;
    // 兼容 id/executionId 字段
    return (
      executions.find((e) => e.executionId === selectedExecutionId) ||
      executions.find((e) => (e as any).id === selectedExecutionId) ||
      null
    );
  }, [executions, selectedExecutionId]);

  const inputFields = useMemo(() => inferInputFields(selectedWorkflow), [selectedWorkflow]);

  // DAG：无执行实例时返回 null（白皮书铁律一：不显示示例假数据）
  const dagExecution = useMemo(() => {
    if (!selectedExecution) return null;
    return executionToDag(selectedExecution);
  }, [selectedExecution]);

  // ===== KPI 计算（从 executions 实时得出） =====
  const kpis = useMemo(() => {
    const runningCount = executions.filter((e) => e.status === WorkflowStatus.Running).length;
    const pendingApproval = executions.filter((e) => e.status === WorkflowStatus.Paused).length;
    const today = new Date().toDateString();
    const successToday = executions.filter(
      (e) =>
        e.status === WorkflowStatus.Completed &&
        e.startedAt &&
        new Date(e.startedAt).toDateString() === today
    ).length;
    const failed = executions.filter((e) => e.status === WorkflowStatus.Failed).length;
    return { runningCount, pendingApproval, successToday, failed, total: executions.length };
  }, [executions]);

  // ===== 运行工作流 =====
  const handleRun = useCallback(async () => {
    if (!selectedWorkflowId) return;
    setRunning(true);
    setRunResult(null);
    setError(null);
    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.Workflow,
        WorkflowCommand.Run,
        {
          workflowId: selectedWorkflowId,
          inputs,
          triggeredBy: "ceo_manual",
        }
      );
      if (resp?.success) {
        // 双层解包：resp.data 是 WorkflowResult，resp.data.data 才是真实数据
        const inner = resp?.data as any;
        const execId = inner?.success
          ? inner.data?.executionId
          : (resp.data as any)?.executionId;
        setRunResult({ success: true, message: `已启动执行：${execId || "未知"}` });
        if (execId) setSelectedExecutionId(execId);
        await loadExecutions();
      } else {
        setRunResult({ success: false, message: resp?.error || "启动失败" });
      }
    } catch (err: any) {
      setRunResult({ success: false, message: err?.message || String(err) });
    } finally {
      setRunning(false);
    }
  }, [selectedWorkflowId, inputs, loadExecutions]);

  // ===== 取消执行 =====
  const handleCancel = useCallback(async (execId: string) => {
    try {
      await RuntimeManager.execute(RuntimeName.Workflow, WorkflowCommand.Cancel, {
        executionId: execId,
      });
      await loadExecutions();
    } catch (err: any) {
      setError(err?.message || String(err));
    }
  }, [loadExecutions]);

  // ===== 重试执行 =====
  const handleRetry = useCallback(async (execId: string) => {
    try {
      const resp = await RuntimeManager.execute(RuntimeName.Workflow, WorkflowCommand.Retry, {
        executionId: execId,
      });
      if (resp?.success) {
        // 双层解包
        const inner = resp?.data as any;
        const newId = inner?.success
          ? inner.data?.newExecutionId
          : (resp.data as any)?.newExecutionId;
        if (newId) setSelectedExecutionId(newId);
        await loadExecutions();
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    }
  }, [loadExecutions]);

  // ===== 审批：通过 / 拒绝 =====
  const handleApprove = useCallback(async (execId: string, approved: boolean) => {
    try {
      await RuntimeManager.execute(RuntimeName.Workflow, WorkflowCommand.Resume, {
        executionId: execId,
        approval: { approved, comment: approved ? "CEO 确认通过" : "CEO 拒绝", approver: "ceo" },
      });
      await loadExecutions();
    } catch (err: any) {
      setError(err?.message || String(err));
    }
  }, [loadExecutions]);

  // ===== 切换选中工作流时，重置输入参数为默认值 =====
  const handleSelectWorkflow = useCallback((wfId: string) => {
    setSelectedWorkflowId(wfId);
    const wf = workflows.find((w) => w.id === wfId);
    setInputs(getDefaultInputs(wf || null));
    setRunResult(null);
  }, [workflows]);

  // ===== 手动刷新 =====
  const handleRefresh = useCallback(async () => {
    await Promise.all([loadWorkflows(), loadExecutions()]);
  }, [loadWorkflows, loadExecutions]);

  // ===== 待审批判断 =====
  const isPaused = selectedExecution?.status === WorkflowStatus.Paused;
  const isRunning = selectedExecution?.status === WorkflowStatus.Running;
  const isFailed = selectedExecution?.status === WorkflowStatus.Failed;

  // ===== 从分组下拉选择功能 → 激活并固定到标签栏 =====
  const handlePickFunction = useCallback((tabId: string) => {
    setActiveTab(tabId);
    setPinnedTabs((prev) => (prev.includes(tabId) ? prev : [...prev, tabId]));
    setOpenGroup(null);
  }, []);

  // ===== 关闭固定 Tab；若为当前激活则回到看板 =====
  const handleUnpin = useCallback((tabId: string) => {
    setPinnedTabs((prev) => prev.filter((t) => t !== tabId));
    setActiveTab((prev) => (prev === tabId ? "workspace" : prev));
  }, []);

  return (
    <div className="h-full w-full overflow-hidden p-3 flex flex-col">
      {/* Phase3 P0-3 业务说明 + 跨模块跳转（经营中心） */}
      <WorkspaceBizBar
        desc="经营中心：自动汇总全链路数据，输出经营诊断建议、调度自动化工作流"
        accent="#06B6D4"
        jumps={[
          { label: "查看总控台", to: "ceo" },
          { label: "查看财务数据", to: "finance" },
        ]}
      />
      {/* ============ 顶部：分组标签栏（relative z-50：防止内容区 transform 层盖住下拉框） ============ */}
      <div className="relative z-50 flex-shrink-0 flex items-center gap-1 px-2 py-1.5 mb-2 rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl flex-wrap">
        {/* 经营看板（默认主战场） */}
        <button
          onClick={() => {
            setActiveTab("workspace");
            setOpenGroup(null);
          }}
          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
            activeTab === "workspace"
              ? "bg-violet-500/15 text-violet-300 border border-violet-500/30"
              : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
          }`}
        >
          <WorkflowIcon size={11} />
          经营看板
        </button>

        <div className="w-px h-4 bg-white/[0.08] mx-1" />

        {/* 分组胶囊：流程 / 协同 / 增长（点击展开下拉选择功能） */}
        {(Object.keys(GROUP_TABS)).map((group) => (
          <div key={group} className="relative">
            <button
              onClick={() => setOpenGroup(openGroup === group ? null : group)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
                openGroup === group
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span>{group}</span>
              <ChevronDown size={10} className={openGroup === group ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>

            {/* 分组下拉菜单 */}
            <AnimatePresence>
              {openGroup === group && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-30"
                    onClick={() => setOpenGroup(null)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-1 z-40 min-w-[150px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl"
                  >
                    {GROUP_TABS[group].map((tab) => {
                      const Icon = tab.icon;
                      const pinned = pinnedTabs.includes(tab.id);
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handlePickFunction(tab.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono text-gray-300 hover:bg-violet-500/10 hover:text-violet-200 transition-colors"
                        >
                          <Icon size={11} className="text-violet-400" />
                          <span className="flex-1 text-left">{tab.label}</span>
                          {activeTab === tab.id && (
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                          )}
                          {pinned && activeTab !== tab.id && (
                            <span className="text-[9px] text-gray-600">已固定</span>
                          )}
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        ))}

        <div className="w-px h-4 bg-white/[0.08] mx-1" />

        {/* 扣子集成（一级入口，外部能力） */}
        <button
          onClick={() => {
            setActiveTab("coze");
            setOpenGroup(null);
          }}
          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
            activeTab === "coze"
              ? "bg-teal-500/15 text-teal-300 border border-teal-500/30"
              : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
          }`}
          title="扣子(Coze) 工作流 / Bot / 智能体集成"
        >
          <Sparkles size={11} className={activeTab === "coze" ? "text-teal-300" : "text-teal-400/70"} />
          扣子
          {cozeConfigured && (
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          )}
        </button>

        {/* 已固定的功能 Tab（可关闭） */}
        {pinnedTabs.length > 0 && <div className="w-px h-4 bg-white/[0.08] mx-1" />}
        {pinnedTabs.map((tabId) => {
          const tabInfo = ALL_FUNCTION_TABS.find((t) => t.id === tabId);
          if (!tabInfo) return null;
          const Icon = tabInfo.icon;
          const isActive = activeTab === tabId;
          return (
            <button
              key={tabId}
              onClick={() => setActiveTab(tabId)}
              className={`group flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono transition ${
                isActive
                  ? "bg-violet-500/15 text-violet-300 border border-violet-500/30"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
              }`}
              title={`${tabInfo.label}（点击切换）`}
            >
              <Icon size={11} />
              {tabInfo.label}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnpin(tabId);
                }}
                className="ml-0.5 p-0.5 rounded hover:bg-white/[0.1] hover:text-red-400 transition-colors"
                title="关闭"
              >
                <X size={9} />
              </span>
            </button>
          );
        })}
      </div>

      {/* ============ 经营看板（驾驶舱主视图） ============ */}
      {activeTab === "workspace" && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto custom-scrollbar"
        >
          {/* 今日目标完成度（Obsidian 真实数据 · compact 折叠不遮挡看板） */}
          <GoalBoardPanel compact />

          {/* KPI 条 + GEO 触发 */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {([
              { label: "运行中", value: kpis.runningCount, color: "#A78BFA", icon: Circle },
              { label: "今日成功", value: kpis.successToday, color: "#34D399", icon: CheckCircle2 },
              { label: "待审批", value: kpis.pendingApproval, color: "#FBBF24", icon: AlertCircle },
              { label: "失败", value: kpis.failed, color: "#F87171", icon: XCircle },
            ] as const).map((k) => {
              const Icon = k.icon;
              return (
                <div
                  key={k.label}
                  className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl"
                >
                  <Icon size={13} style={{ color: k.color }} />
                  <div>
                    <div className="text-[15px] font-semibold leading-none" style={{ color: k.color }}>
                      {k.value}
                    </div>
                    <div className="text-[9px] font-mono text-gray-500 mt-0.5">{k.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 经营情报区：AI 经营建议 / 官网访问统计 双 Tab（v6.37，避免长内容占用纵向空间） */}
          <div className="flex-shrink-0 rounded-lg border border-white/[0.06] bg-white/[0.02] flex flex-col">
            <div className="flex items-center gap-1 px-2 pt-2 flex-shrink-0">
              <TabButton active={adviceTab === "advice"} onClick={() => setAdviceTab("advice")}>
                🧠 AI 经营建议
              </TabButton>
              <TabButton active={adviceTab === "stats"} onClick={() => setAdviceTab("stats")}>
                🌐 官网访问统计
              </TabButton>
              <TabButton active={adviceTab === "review"} onClick={() => setAdviceTab("review")}>
                📊 效果复盘
              </TabButton>
            </div>
            <div>
              {adviceTab === "advice" ? (
                <BusinessAdvicePanel />
              ) : adviceTab === "stats" ? (
                <WebsiteStatsPanel />
              ) : (
                <EffectReviewPanel />
              )}
            </div>
          </div>

          {/* Phase3 P1-3：高级工作流控制台折叠开关（DAG / 执行历史 / 调度） */}
          <div className="flex-shrink-0 flex items-center justify-between gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5">
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-gray-400">
              <WorkflowIcon size={11} className="text-violet-400" />
              <span>高级工作流控制台</span>
              <span className="text-gray-600">DAG 可视化 · 执行历史 · 调度</span>
            </div>
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
            >
              {showAdvanced ? "收起" : "展开"}
              <ChevronDown size={11} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
            </button>
          </div>

          {/* 主体三区：控制台 / 执行详情 / 运行历史（默认折叠，老板/运营只看经营建议） */}
          {showAdvanced && (
          <div className="flex-1 min-h-[440px] grid grid-cols-12 gap-3">
            {/* ============ 左 4/12：工作流控制台（内部工作流 + 扣子 Bot） ============ */}
            <div className="col-span-4 min-h-0 flex flex-col rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden">
              <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <Terminal size={11} className="text-violet-400" />
                  <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
                    工作流控制台
                  </span>
                </div>
                <span className="text-[9px] font-mono text-gray-500">
                  {workflows.length + cozeBots.length} 项
                </span>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {/* 内部工作流分区 */}
                <div className="px-3 pt-2 pb-1 text-[9px] font-mono text-gray-600 uppercase tracking-wider flex items-center gap-1">
                  <WorkflowIcon size={9} /> 内部工作流（{workflows.length}）
                </div>
                {loadingWf ? (
                  <div className="p-3 text-[10px] text-gray-500 font-mono flex items-center gap-2">
                    <Loader2 size={10} className="animate-spin" /> 加载中...
                  </div>
                ) : workflows.length === 0 ? (
                  <div className="px-3 py-2 text-[10px] text-gray-500 font-mono">暂无注册工作流</div>
                ) : (
                  workflows.map((wf) => {
                    const selected = wf.id === selectedWorkflowId;
                    return (
                      <button
                        key={wf.id}
                        onClick={() => handleSelectWorkflow(wf.id)}
                        className={`w-full text-left px-3 py-2 border-b border-white/[0.03] transition-colors ${
                          selected
                            ? "bg-violet-500/10 border-l-2 border-l-violet-400"
                            : "hover:bg-white/[0.03] border-l-2 border-l-transparent"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <div className={`text-[11px] font-medium truncate ${selected ? "text-violet-300" : "text-gray-200"}`}>
                              {wf.name}
                            </div>
                            <div className="text-[9px] text-gray-500 font-mono truncate mt-0.5">
                              {wf.id}
                            </div>
                            {wf.description && (
                              <div className="text-[9px] text-gray-500 mt-1 line-clamp-2">
                                {wf.description}
                              </div>
                            )}
                          </div>
                          <ChevronRight
                            size={10}
                            className={`flex-shrink-0 mt-0.5 ${selected ? "text-violet-400" : "text-gray-600"}`}
                          />
                        </div>
                        {wf.tags && wf.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {wf.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="px-1 py-0.5 rounded text-[8px] font-mono bg-white/[0.04] text-gray-400 border border-white/[0.04]"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}

                {/* 扣子 Bot 分区（外部能力，带 Coze 徽标） */}
                <div className="px-3 pt-3 pb-1 text-[9px] font-mono uppercase tracking-wider flex items-center gap-1 text-teal-400/80">
                  <Sparkles size={9} /> 扣子 Bot（{cozeBots.length}）
                </div>
                {cozeBots.length === 0 ? (
                  <button
                    onClick={() => setActiveTab("coze")}
                    className="w-full text-left px-3 py-2 text-[10px] font-mono text-teal-300/70 hover:text-teal-300 hover:bg-teal-500/5 transition-colors"
                  >
                    {cozeConfigured ? "接入扣子 Bot →" : "配置扣子 API Key 接入 →"}
                  </button>
                ) : (
                  cozeBots.map((bot) => (
                    <button
                      key={bot.bot_id}
                      onClick={() => setActiveTab("coze")}
                      className="w-full text-left px-3 py-2 border-b border-white/[0.03] hover:bg-teal-500/5 transition-colors flex items-center gap-2"
                    >
                      <Bot size={11} className="text-teal-400 flex-shrink-0" />
                      <span className="text-[11px] text-gray-200 truncate flex-1">{bot.name}</span>
                      <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-teal-500/10 text-teal-400 flex-shrink-0">
                        Coze
                      </span>
                    </button>
                  ))
                )}
              </div>

              {/* 输入参数表单（选中工作流时显示） */}
              {selectedWorkflow && (
                <div className="border-t border-white/[0.04] flex-shrink-0 max-h-[45%] flex flex-col">
                  <div className="px-3 py-2 border-b border-white/[0.04] flex items-center gap-1.5">
                    <FileText size={11} className="text-amber-400" />
                    <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
                      输入参数 · {selectedWorkflow.name}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {inputFields.length === 0 ? (
                      <div className="text-[9px] text-gray-500 font-mono">此工作流无输入参数</div>
                    ) : (
                      inputFields.map((field) => (
                        <div key={field.key}>
                          <label className="block text-[9px] font-mono text-gray-400 mb-1">
                            {field.label}
                            <span className="text-gray-600 ml-1">({field.key})</span>
                          </label>
                          {field.type === "boolean" ? (
                            <button
                              onClick={() =>
                                setInputs((prev) => ({ ...prev, [field.key]: !prev[field.key] }))
                              }
                              className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-mono transition-colors ${
                                inputs[field.key]
                                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                                  : "bg-white/[0.03] border-white/[0.06] text-gray-400"
                              }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  inputs[field.key] ? "bg-emerald-400" : "bg-gray-600"
                                }`}
                              />
                              {inputs[field.key] ? "true" : "false"}
                            </button>
                          ) : (
                            <input
                              type={field.type === "number" ? "number" : "text"}
                              value={
                                field.type === "list"
                                  ? Array.isArray(inputs[field.key])
                                    ? (inputs[field.key] as any[]).join(",")
                                    : String(inputs[field.key] ?? "")
                                  : String(inputs[field.key] ?? "")
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                setInputs((prev) => ({
                                  ...prev,
                                  [field.key]:
                                    field.type === "list"
                                      ? v.split(",").map((s) => s.trim()).filter(Boolean)
                                      : field.type === "number"
                                      ? Number(v)
                                      : v,
                                }));
                              }}
                              className="w-full px-2 py-1 rounded bg-black/30 border border-white/[0.06] text-[10px] font-mono text-gray-200 focus:outline-none focus:border-violet-500/40"
                            />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {/* 运行按钮 */}
                  <div className="p-3 border-t border-white/[0.04] space-y-2">
                    <button
                      onClick={handleRun}
                      disabled={running || !selectedWorkflowId}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                    >
                      {running ? (
                        <Loader2 size={12} className="animate-spin text-emerald-400" />
                      ) : (
                        <Play size={12} className="text-emerald-400" />
                      )}
                      <span className="text-[11px] font-medium text-emerald-300">
                        {running ? "运行中..." : "运行工作流"}
                      </span>
                    </button>
                    {runResult && (
                      <div
                        className={`text-[9px] font-mono px-2 py-1 rounded border ${
                          runResult.success
                            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                            : "bg-red-500/10 border-red-500/30 text-red-300"
                        }`}
                      >
                        {runResult.message}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ============ 中 5/12：DAG + 步骤结果 + 审批面板 ============ */}
            <div className="col-span-5 min-h-0 flex flex-col gap-3">
              {/* DAG 可视化（真实数据，无执行显示空态） */}
              <div className="flex-1 min-h-[240px] relative rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden">
                <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <WorkflowIcon size={11} className="text-violet-400" />
                    <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
                      DAG 可视化
                    </span>
                  </div>
                  {dagExecution && (
                    <div className="text-[9px] font-mono text-gray-500">
                      {dagExecution.workflowName} · {dagExecution.completedNodes}/{dagExecution.totalNodes}
                    </div>
                  )}
                </div>
                <div className="h-[calc(100%-30px)]">
                  {dagExecution ? (
                    <WorkflowLiveDag execution={dagExecution} />
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <WorkflowIcon size={18} className="mx-auto text-gray-600 mb-2" />
                        <div className="text-[11px] text-gray-500 font-mono">尚无执行记录</div>
                        <div className="text-[9px] text-gray-600 mt-1">
                          从左侧选择工作流并运行，DAG 将实时展示
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {error && (
                  <div className="absolute top-9 right-3 px-2 py-1 rounded border border-red-500/30 bg-red-500/10 text-[9px] font-mono text-red-400">
                    {error}
                  </div>
                )}
              </div>

              {/* 步骤结果 / 审批面板 */}
              <div className="flex-1 min-h-[180px] rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden flex flex-col">
                <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Terminal size={11} className="text-amber-400" />
                    <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
                      步骤执行结果
                    </span>
                  </div>
                  {selectedExecution && (
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{
                          background: STEP_COLOR_MAP[selectedExecution.status] || "#6B7280",
                        }}
                      />
                      <span className="text-[9px] font-mono text-gray-400">
                        {EXEC_STATUS_LABEL[selectedExecution.status] || selectedExecution.status}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  {!selectedExecution ? (
                    <div className="text-[10px] text-gray-500 font-mono p-3">
                      请从右栏选择执行实例查看步骤详情
                    </div>
                  ) : isPaused ? (
                    /* 审批面板 */
                    <div className="p-3 space-y-3">
                      <div className="flex items-start gap-2 p-2.5 rounded-md border border-amber-500/30 bg-amber-500/10">
                        <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-[11px] font-medium text-amber-300">
                            等待人工审批
                          </div>
                          <div className="text-[9px] font-mono text-amber-400/70 mt-1">
                            执行 ID: {selectedExecution.executionId}
                          </div>
                          <div className="text-[10px] text-gray-300 mt-1.5">
                            工作流 [{selectedExecution.workflowName}] 已暂停，请确认是否继续执行后续步骤。
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(selectedExecution.executionId, true)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors"
                        >
                          <CheckSquare size={12} className="text-emerald-400" />
                          <span className="text-[11px] font-medium text-emerald-300">批准继续</span>
                        </button>
                        <button
                          onClick={() => handleApprove(selectedExecution.executionId, false)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-red-500/15 border border-red-500/40 hover:bg-red-500/25 transition-colors"
                        >
                          <XSquare size={12} className="text-red-400" />
                          <span className="text-[11px] font-medium text-red-300">拒绝并终止</span>
                        </button>
                      </div>
                    </div>
                  ) : (selectedExecution.stepResults?.length || 0) === 0 ? (
                    <div className="text-[10px] text-gray-500 font-mono p-3">
                      暂无步骤结果（执行尚未开始或步骤未产出）
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {(selectedExecution.stepResults || []).map((step: StepResult, idx: number) => {
                        const color = STEP_COLOR_MAP[step.status] || "#6B7280";
                        return (
                          <div
                            key={step.stepId || idx}
                            className="px-2 py-1.5 rounded border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <div
                                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                  style={{ background: color }}
                                />
                                <span className="text-[10px] font-mono text-gray-300 truncate">
                                  {step.stepName || step.stepId}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {step.duration !== undefined && (
                                  <span className="text-[9px] font-mono text-gray-500">
                                    {step.duration < 1000
                                      ? `${step.duration}ms`
                                      : `${(step.duration / 1000).toFixed(1)}s`}
                                  </span>
                                )}
                                <span
                                  className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                                  style={{ background: `${color}20`, color }}
                                >
                                  {STEP_STATUS_LABEL[step.status] || step.status}
                                </span>
                              </div>
                            </div>
                            {step.error && (
                              <div className="mt-1 px-2 py-1 rounded bg-red-500/10 border border-red-500/20 text-[9px] font-mono text-red-300 overflow-x-auto">
                                {step.error}
                              </div>
                            )}
                            {step.output !== undefined && step.output !== null && (
                              <details className="mt-1">
                                <summary className="text-[9px] font-mono text-gray-500 cursor-pointer hover:text-gray-300">
                                  查看输出
                                </summary>
                                <pre className="mt-1 px-2 py-1 rounded bg-black/30 border border-white/[0.04] text-[9px] font-mono text-gray-400 overflow-x-auto max-h-32">
                                  {typeof step.output === "string"
                                    ? step.output.slice(0, 500)
                                    : JSON.stringify(step.output, null, 2).slice(0, 500)}
                                </pre>
                              </details>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ============ 右 3/12：执行历史 + 快捷操作 ============ */}
            <div className="col-span-3 min-h-0 flex flex-col rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden">
              <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <Clock size={11} className="text-violet-400" />
                  <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
                    执行历史
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-500">{executions.length}</span>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-50"
                    title="刷新"
                  >
                    <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingExec ? (
                  <div className="p-3 text-[10px] text-gray-500 font-mono flex items-center gap-2">
                    <Loader2 size={10} className="animate-spin" /> 加载中...
                  </div>
                ) : executions.length === 0 ? (
                  <div className="p-3 text-[10px] text-gray-500 font-mono">
                    暂无执行历史
                    <div className="mt-2 text-[9px] text-gray-600">
                      从左栏选择工作流并点击"运行"按钮启动首次执行
                    </div>
                  </div>
                ) : (
                  executions.map((exec) => {
                    const selected = exec.executionId === selectedExecutionId;
                    const color = STEP_COLOR_MAP[exec.status] || "#6B7280";
                    return (
                      <div
                        key={exec.executionId}
                        onClick={() => setSelectedExecutionId(exec.executionId)}
                        className={`px-3 py-2 border-b border-white/[0.03] cursor-pointer transition-colors ${
                          selected
                            ? "bg-violet-500/10 border-l-2 border-l-violet-400"
                            : "hover:bg-white/[0.03] border-l-2 border-l-transparent"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: color }}
                            />
                            <span
                              className={`text-[10px] font-mono truncate ${
                                selected ? "text-violet-300" : "text-gray-300"
                              }`}
                            >
                              {exec.workflowName || exec.workflowId}
                            </span>
                          </div>
                          <span
                            className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0"
                            style={{ background: `${color}20`, color }}
                          >
                            {EXEC_STATUS_LABEL[exec.status] || exec.status}
                          </span>
                        </div>
                        <div className="text-[9px] font-mono text-gray-500 truncate">
                          {exec.executionId}
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[9px] font-mono text-gray-600">
                            {exec.startedAt
                              ? new Date(exec.startedAt).toLocaleString("zh-CN", {
                                  hour12: false,
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })
                              : "--"}
                          </span>
                          {/* 操作按钮 */}
                          <div className="flex items-center gap-1">
                            {exec.status === WorkflowStatus.Running && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancel(exec.executionId);
                                }}
                                title="取消执行"
                                className="p-1 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                              >
                                <XCircle size={11} />
                              </button>
                            )}
                            {exec.status === WorkflowStatus.Failed && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRetry(exec.executionId);
                                }}
                                title="重试"
                                className="p-1 rounded hover:bg-amber-500/20 text-amber-400 transition-colors"
                              >
                                <RotateCw size={11} />
                              </button>
                            )}
                            {exec.status === WorkflowStatus.Completed && (
                              <CheckCircle2 size={11} className="text-emerald-400/60" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* 当前选中实例的快捷操作 */}
              {selectedExecution && (
                <div className="border-t border-white/[0.04] p-2 flex items-center gap-1 flex-shrink-0">
                  {isRunning && (
                    <button
                      onClick={() => handleCancel(selectedExecution.executionId)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 transition-colors"
                    >
                      <XCircle size={10} className="text-red-400" />
                      <span className="text-[10px] font-mono text-red-300">取消</span>
                    </button>
                  )}
                  {isFailed && (
                    <button
                      onClick={() => handleRetry(selectedExecution.executionId)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
                    >
                      <RotateCw size={10} className="text-amber-400" />
                      <span className="text-[10px] font-mono text-amber-300">重试</span>
                    </button>
                  )}
                  {!isRunning && !isFailed && (
                    <div className="flex-1 text-center text-[9px] font-mono text-gray-500">
                      状态: {EXEC_STATUS_LABEL[selectedExecution.status]}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}
        </motion.div>
      )}

      {/* ============ 功能 Tab 内容区 ============ */}
      {activeTab !== "workspace" && activeTab !== "coze" && (
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="flex-1 min-h-0 overflow-hidden"
        >
          {activeTab === "templates" && <WorkflowTemplateLibrary />}
          {activeTab === "editor" && <WorkflowNodeEditor />}
          {activeTab === "monitor" && <WorkflowMonitor />}
          {activeTab === "sop" && <SOPLibrary />}
          {activeTab === "hermes_tasks" && <HermesTaskPanel />}
          {activeTab === "efficiency" && <WorkflowEfficiencyAnalyzer />}
          {activeTab === "human_ai" && <HumanAICollaboration />}
          {activeTab === "approval" && <ApprovalFlowEngine />}

          {/* P2 组件 */}
          {activeTab === "optimize" && <ProcessAutoOptimizer />}
          {activeTab === "event" && <EventDrivenAutomation />}
          {activeTab === "dispatcher" && <ProductDispatcherPanel />}
          {activeTab === "decision" && <DecisionSupportPanel />}
          {activeTab === "digital_twin" && <OperationsDigitalTwin />}
          {activeTab === "website" && <WebsiteControlPanel />}
        </motion.div>
      )}

      {/* ============ 扣子集成（一级入口，全屏面板） ============ */}
      {activeTab === "coze" && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="flex-1 min-h-0 overflow-hidden"
        >
          <CozeWorkflowPanel />
        </motion.div>
      )}
    </div>
  );
}
